/**
 * ProfileResetPasswordSection — reset-password block with feedback states.
 * Extracted verbatim from ProfilePage.jsx (god-file decomposition).
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

export default function ProfileResetPasswordSection({ email }) {
    const [resetStatus, setResetStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
    const [resetError, setResetError] = useState('');
    
    const handleResetPassword = async () => {
        if (!email) {
            setResetStatus('error');
            setResetError('Please enter your email address first');
            return;
        }
        
        setResetStatus('sending');
        setResetError('');
        
        try {
            // Public, rate-limited reset endpoint (no auth required, no enumeration)
            const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/requestPasswordReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send reset email');
            }

            setResetStatus('sent');
            
            // Reset to idle after 5 seconds
            setTimeout(() => {
                setResetStatus('idle');
            }, 5000);
        } catch (error) {
            console.error('Reset password error:', error);
            setResetStatus('error');
            setResetError(error.message || 'Failed to send reset email');
            
            // Reset to idle after 5 seconds
            setTimeout(() => {
                setResetStatus('idle');
                setResetError('');
            }, 5000);
        }
    };
    
    return (
        <View style={{ marginTop: 16 }}>
            <Text style={{ 
                color: 'rgba(255,255,255,0.8)', 
                fontSize: 14, 
                fontWeight: '600',
                marginBottom: 8 
            }}>
                Password
            </Text>
            <TouchableOpacity
                disabled={resetStatus === 'sending'}
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    padding: 12,
                    backgroundColor: resetStatus === 'sent' 
                        ? 'rgba(74, 222, 128, 0.15)' 
                        : resetStatus === 'error' 
                            ? 'rgba(248, 113, 113, 0.15)'
                            : 'rgba(255,255,255,0.05)',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: resetStatus === 'sent' 
                        ? 'rgba(74, 222, 128, 0.4)' 
                        : resetStatus === 'error' 
                            ? 'rgba(248, 113, 113, 0.4)'
                            : 'rgba(255,255,255,0.1)',
                    opacity: resetStatus === 'sending' ? 0.7 : 1,
                }}
                onPress={handleResetPassword}
            >
                <Ionicons 
                    name={
                        resetStatus === 'sent' ? 'checkmark-circle' : 
                        resetStatus === 'error' ? 'alert-circle' : 
                        resetStatus === 'sending' ? 'time' : 'key-outline'
                    } 
                    size={18} 
                    color={
                        resetStatus === 'sent' ? '#4ade80' : 
                        resetStatus === 'error' ? '#f87171' : 
                        'rgba(255,255,255,0.7)'
                    } 
                />
                <Text style={{ 
                    color: resetStatus === 'sent' ? '#4ade80' : 
                           resetStatus === 'error' ? '#f87171' : 
                           'rgba(255,255,255,0.8)', 
                    fontSize: 14, 
                    fontWeight: '500',
                    flex: 1
                }}>
                    {resetStatus === 'sending' ? 'Sending Reset Email...' :
                     resetStatus === 'sent' ? '✓ Reset Email Sent!' :
                     resetStatus === 'error' ? 'Failed - Try Again' :
                     'Reset Password'}
                </Text>
                {resetStatus === 'idle' && (
                    <Ionicons name="chevron-forward" size={16} color="rgba(255,255,255,0.5)" />
                )}
            </TouchableOpacity>
            <Text style={{ 
                color: resetStatus === 'sent' ? '#4ade80' : 
                       resetStatus === 'error' ? '#f87171' : 
                       'rgba(255,255,255,0.5)', 
                fontSize: 12, 
                marginTop: 6 
            }}>
                {resetStatus === 'sent' 
                    ? 'Check your inbox for the password reset link'
                    : resetStatus === 'error' 
                        ? resetError
                        : 'Send a password reset link to your email address'
                }
            </Text>
        </View>
    );
};
